import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Skeleton,
  Link,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetOutsourcedApplicationDetails,
  apiShortlistCandidate,
  apiScheduleOutsourcedInterview,
  apiHireOutsourcedCandidate,
} from "../../api/outsourcing";
import { useNotificationService } from "../../components/notification";
import { formatDate } from "../../utils/url-id";
import { ArrowBack } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

const OutsourcedApplicationDetails = () => {
  const navigate = useNavigate();
  const { requestId, applicationId } = useParams<{
    requestId: string;
    applicationId: string;
  }>();
  const { showNotification } = useNotificationService();
  const queryClient = useQueryClient();

  const [shortlistDialogOpen, setShortlistDialogOpen] = useState(false);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [hireDialogOpen, setHireDialogOpen] = useState(false);

  const [shortlistNotes, setShortlistNotes] = useState("");
  const [interviewData, setInterviewData] = useState({
    interview_date: "",
    location: "",
    details: "",
    type: "virtual",
  });
  const [hireData, setHireData] = useState({
    salary: "",
    employment_type: "full-time",
    work_type: "on-site",
    start_date: "",
    end_date: "",
    contract_duration: "",
    position: "",
    benefits: "",
    note: "",
  });

  // Fetch application details
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["outsourced-application-details", requestId, applicationId],
    queryFn: () =>
      apiGetOutsourcedApplicationDetails({
        requestId: requestId!,
        applicationId: applicationId!,
      }),
    enabled: !!requestId && !!applicationId,
  });

  const application = data?.data?.data?.application;
  const medic = data?.data?.data?.medic;
  const request = data?.data?.data?.outsourcing_request;

  // Shortlist mutation
  const shortlistMutation = useMutation({
    mutationFn: () =>
      apiShortlistCandidate({
        requestId: requestId!,
        applicationId: applicationId!,
        notes: shortlistNotes,
      }),
    onSuccess: (data) => {
      if (data?.status === "error") {
        showNotification(
          data?.data.msg ?? "Failed to shortlist candidate",
          "error",
        );
        return;
      }
      showNotification("Candidate shortlisted successfully", "success");
      setShortlistDialogOpen(false);
      refetch();
      queryClient.invalidateQueries(["outsourced-applications"] as any);
    },
    onError: () => {
      showNotification("Failed to shortlist candidate", "error");
    },
  });

  // Interview mutation
  const interviewMutation = useMutation({
    mutationFn: () =>
      apiScheduleOutsourcedInterview({
        requestId: requestId!,
        applicationId: applicationId!,
        ...interviewData,
      }),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      showNotification("Interview scheduled successfully", "success");
      setInterviewDialogOpen(false);
      refetch();
    },
    onError: () => {
      showNotification("Failed to schedule interview", "error");
    },
  });

  // Hire mutation
  const hireMutation = useMutation({
    mutationFn: () =>
      apiHireOutsourcedCandidate({
        requestId: requestId!,
        application_id: applicationId!,
        salary: parseFloat(hireData.salary),
        employment_type: hireData.employment_type,
        work_type: hireData.work_type,
        start_date: hireData.start_date,
        end_date: hireData.end_date || undefined,
        contract_duration: hireData.contract_duration || undefined,
        position: hireData.position,
        benefits: hireData.benefits
          ? hireData.benefits.split(",").map((b) => b.trim())
          : undefined,
        note: hireData.note || undefined,
      }),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "Failed to hire candidate", "error");
          return;
        }
      }
      showNotification("Candidate hired successfully", "success");
      setHireDialogOpen(false);
      refetch();
      queryClient.invalidateQueries(["outsourced-applications"] as any);
      queryClient.invalidateQueries(["outsourced-requests"] as any);
    },
    onError: () => {
      showNotification("Failed to hire candidate", "error");
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const getStatusColor = (status: string) => {
    const colors: Record<
      string,
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
    > = {
      pending: "warning",
      shortlisted: "info",
      interviewing: "primary",
      accepted: "success",
      rejected: "error",
    };
    return colors[status] || "default";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/outsourcing/${requestId}/applications`)}
        sx={{ mb: 2 }}
      >
        Back to Applications
      </Button>

      {/* Application Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="start"
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {medic?.first_name} {medic?.last_name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={application?.medic_info.medic_type} />
                <Chip
                  label={application?.status.toUpperCase()}
                  color={getStatusColor(application?.status)}
                />
              </Stack>
              <Typography variant="body2" color="textSecondary">
                <strong>Email:</strong> {medic?.email}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Phone:</strong> {medic?.phone_number || "N/A"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Location:</strong> {application?.medic_info.state}
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShortlistDialogOpen(true)}
                disabled={
                  application?.status === "shortlisted" ||
                  application?.status === "accepted"
                }
              >
                Shortlist
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setInterviewDialogOpen(true)}
                disabled={application?.status === "accepted"}
              >
                Schedule Interview
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => setHireDialogOpen(true)}
                disabled={application?.status === "accepted"}
              >
                Hire Candidate
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Job Details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Job Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Position:</strong> {request?.job_info.job_title}
                </Typography>
                <Typography variant="body2">
                  <strong>Job Type:</strong> {request?.job_info.job_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Department:</strong> {request?.job_info.department}
                </Typography>
                <Typography variant="body2">
                  <strong>Work Location:</strong>{" "}
                  {request?.job_info.work_location}
                </Typography>
                <Typography variant="body2">
                  <strong>Salary Range:</strong>{" "}
                  {request?.job_info.salary_range}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Facility Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Name:</strong> {request?.facility_info.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {request?.facility_info.facility_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Location:</strong> {request?.facility_info.city},{" "}
                  {request?.facility_info.state}
                </Typography>
                <Typography variant="body2">
                  <strong>Contact:</strong>{" "}
                  {request?.facility_info.contact_email}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Interview Details (if scheduled) */}
        {application?.interview && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Interview Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Date:</strong>{" "}
                      {formatDate(application.interview.interview_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Type:</strong> {application.interview.type}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Location:</strong>{" "}
                      {application.interview.location}
                    </Typography>
                  </Grid>
                  {application.interview.link && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Link:</strong>{" "}
                        <Link
                          href={application.interview.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {application.interview.link}
                        </Link>
                      </Typography>
                    </Grid>
                  )}

                  {application.interview.details && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Details:</strong>{" "}
                        {application.interview.details}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Application Documents */}
        {application?.documents_url && application.documents_url.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Documents
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  {application.documents_url.map(
                    (doc: string, index: number) => (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        href={doc}
                        target="_blank"
                        sx={{ justifyContent: "flex-start" }}
                      >
                        Document {index + 1}
                      </Button>
                    ),
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Notes */}
        {application?.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2">{application.notes}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Shortlist Dialog */}
      <Dialog
        open={shortlistDialogOpen}
        onClose={() => setShortlistDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>Shortlist Candidate</DialogTitle>
        <DialogContent>
          <TextField
            label="Notes (Optional)"
            multiline
            rows={4}
            fullWidth
            value={shortlistNotes}
            onChange={(e) => setShortlistNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortlistDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={() => shortlistMutation.mutate()}
            loading={shortlistMutation.isPending}
          >
            Shortlist
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Interview Dialog */}
      <Dialog
        open={interviewDialogOpen}
        onClose={() => setInterviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>Schedule Interview</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Interview Date & Time *"
              type="datetime-local"
              fullWidth
              value={interviewData.interview_date}
              onChange={(e) =>
                setInterviewData({
                  ...interviewData,
                  interview_date: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Type *"
              select
              fullWidth
              value={interviewData.type}
              onChange={(e) =>
                setInterviewData({ ...interviewData, type: e.target.value })
              }
            >
              <MenuItem value="physical">physical</MenuItem>
              <MenuItem value="virtual">virtual</MenuItem>
            </TextField>
            <TextField
              label="Location/Link *"
              fullWidth
              value={interviewData.location}
              onChange={(e) =>
                setInterviewData({ ...interviewData, location: e.target.value })
              }
              placeholder="Physical address or video call link"
            />
            <TextField
              label="Additional Details"
              multiline
              rows={3}
              fullWidth
              value={interviewData.details}
              onChange={(e) =>
                setInterviewData({ ...interviewData, details: e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterviewDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={() => interviewMutation.mutate()}
            loading={interviewMutation.isPending}
          >
            Schedule Interview
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Hire Dialog */}
      <Dialog
        open={hireDialogOpen}
        onClose={() => setHireDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>Hire Candidate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Position *"
                fullWidth
                value={hireData.position}
                onChange={(e) =>
                  setHireData({ ...hireData, position: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Salary *"
                type="number"
                fullWidth
                value={hireData.salary}
                onChange={(e) =>
                  setHireData({ ...hireData, salary: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Employment Type *"
                select
                fullWidth
                value={hireData.employment_type}
                onChange={(e) =>
                  setHireData({ ...hireData, employment_type: e.target.value })
                }
              >
                <MenuItem value="full-time">Full-Time</MenuItem>
                <MenuItem value="part-time">Part-Time</MenuItem>
                <MenuItem value="contract">Contract</MenuItem>
                <MenuItem value="temporary">Temporary</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Work Type *"
                select
                fullWidth
                value={hireData.work_type}
                onChange={(e) =>
                  setHireData({ ...hireData, work_type: e.target.value })
                }
              >
                <MenuItem value="on-site">On-Site</MenuItem>
                <MenuItem value="remote">Remote</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date *"
                type="date"
                fullWidth
                value={hireData.start_date}
                onChange={(e) =>
                  setHireData({ ...hireData, start_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date (Optional)"
                type="date"
                fullWidth
                value={hireData.end_date}
                onChange={(e) =>
                  setHireData({ ...hireData, end_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Contract Duration (Optional)"
                fullWidth
                value={hireData.contract_duration}
                onChange={(e) =>
                  setHireData({
                    ...hireData,
                    contract_duration: e.target.value,
                  })
                }
                placeholder="e.g., 6 months, 1 year"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Benefits (Optional, comma-separated)"
                fullWidth
                value={hireData.benefits}
                onChange={(e) =>
                  setHireData({ ...hireData, benefits: e.target.value })
                }
                placeholder="Health insurance, 401k, PTO"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Additional Notes"
                multiline
                rows={3}
                fullWidth
                value={hireData.note}
                onChange={(e) =>
                  setHireData({ ...hireData, note: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHireDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="success"
            onClick={() => hireMutation.mutate()}
            loading={hireMutation.isPending}
          >
            Hire Candidate
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutsourcedApplicationDetails;
